// Renderizador do subconjunto de Markdown usado nos artigos de ajuda. Produz
// elementos React, nunca HTML bruto: o conteúdo é nosso, mas a console não
// abre exceção para innerHTML.
//
// Blocos: "## " e "### " títulos, "- " lista, "1. " lista numerada, "> " nota
// e ">! " atenção, ``` código, "| a | b |" tabela (com linha "|---|") e
// parágrafo. Em linha: **negrito**, `código` e [texto](destino), onde o
// destino é "#artigo" (outro artigo da ajuda), "/rota" (tela da organização)
// ou http(s) (abre em outra aba).
import { Info, TriangleAlert } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { Link } from "react-router";

export type Block =
  | { kind: "h2" | "h3"; text: string; id: string }
  | { kind: "p"; text: string }
  | { kind: "ul" | "ol"; items: string[] }
  | { kind: "note"; tone: "info" | "warn"; text: string }
  | { kind: "code"; text: string }
  | { kind: "table"; head: string[]; rows: string[][] };

/** Identificador de âncora a partir de um título: minúsculas, sem acento, hífens. */
export function anchor(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function splitRow(line: string): string[] {
  return line.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
}

export function parse(src: string): Block[] {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  const para: string[] = [];
  const flush = () => {
    if (para.length) {
      blocks.push({ kind: "p", text: para.join(" ") });
      para.length = 0;
    }
  };

  while (i < lines.length) {
    const line = lines[i] ?? "";
    const trimmed = line.trim();

    if (trimmed === "") { flush(); i++; continue; }

    if (trimmed.startsWith("```")) {
      flush();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? "").trim().startsWith("```")) { buf.push(lines[i] ?? ""); i++; }
      i++;
      blocks.push({ kind: "code", text: buf.join("\n") });
      continue;
    }

    const h = /^(##|###) +(.+)$/.exec(trimmed);
    if (h) {
      flush();
      const text = (h[2] ?? "").trim();
      blocks.push({ kind: h[1] === "##" ? "h2" : "h3", text, id: anchor(text) });
      i++;
      continue;
    }

    if (/^>!? ?/.test(trimmed)) {
      flush();
      const warn = trimmed.startsWith(">!");
      const buf: string[] = [];
      // Linhas seguidas do mesmo tipo formam uma nota; mudar de "> " para ">! " começa outra.
      while (i < lines.length && /^>!? ?/.test((lines[i] ?? "").trim()) && (lines[i] ?? "").trim().startsWith(">!") === warn) {
        buf.push((lines[i] ?? "").trim().replace(/^>!? ?/, ""));
        i++;
      }
      blocks.push({ kind: "note", tone: warn ? "warn" : "info", text: buf.join(" ") });
      continue;
    }

    if (/^[-*] /.test(trimmed)) {
      flush();
      const items: string[] = [];
      while (i < lines.length && /^[-*] /.test((lines[i] ?? "").trim())) { items.push((lines[i] ?? "").trim().slice(2)); i++; }
      blocks.push({ kind: "ul", items });
      continue;
    }

    if (/^\d+\. /.test(trimmed)) {
      flush();
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test((lines[i] ?? "").trim())) { items.push((lines[i] ?? "").trim().replace(/^\d+\. /, "")); i++; }
      blocks.push({ kind: "ol", items });
      continue;
    }

    if (trimmed.startsWith("|")) {
      flush();
      const rows: string[][] = [];
      while (i < lines.length && (lines[i] ?? "").trim().startsWith("|")) {
        const row = (lines[i] ?? "").trim();
        if (!/^\|[\s:-]+\|/.test(row) || !/^[|\s:-]+$/.test(row)) rows.push(splitRow(row));
        i++;
      }
      const [head = [], ...body] = rows;
      blocks.push({ kind: "table", head, rows: body });
      continue;
    }

    para.push(trimmed);
    i++;
  }
  flush();
  return blocks;
}

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

/** Texto com negrito, código e links resolvidos; `base` é a rota da organização. */
export function Inline({ text, base }: { text: string; base: string }) {
  const parts = text.split(INLINE);
  return (
    <>
      {parts.map((part, k) => {
        if (part.startsWith("**") && part.endsWith("**")) return <strong key={k}>{part.slice(2, -2)}</strong>;
        if (part.startsWith("`") && part.endsWith("`")) return <code key={k}>{part.slice(1, -1)}</code>;
        const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
        if (link) {
          const [, label = "", target = ""] = link;
          if (target.startsWith("#")) return <Link key={k} to={`${base}/help/${target.slice(1)}`}>{label}</Link>;
          if (target.startsWith("/")) return <Link key={k} to={`${base}${target}`}>{label}</Link>;
          if (/^https?:\/\//.test(target)) return <a key={k} href={target} target="_blank" rel="noreferrer">{label}</a>;
          return <Fragment key={k}>{label}</Fragment>;
        }
        return <Fragment key={k}>{part}</Fragment>;
      })}
    </>
  );
}

export function Markdown({ source, base }: { source: string; base: string }): ReactNode {
  const blocks = parse(source);
  return (
    <div className="prose">
      {blocks.map((b, k) => {
        switch (b.kind) {
          case "h2": return <h2 key={k} id={b.id}><Inline text={b.text} base={base} /></h2>;
          case "h3": return <h3 key={k} id={b.id}><Inline text={b.text} base={base} /></h3>;
          case "p": return <p key={k}><Inline text={b.text} base={base} /></p>;
          case "ul": return <ul key={k}>{b.items.map((it, j) => <li key={j}><Inline text={it} base={base} /></li>)}</ul>;
          case "ol": return <ol key={k}>{b.items.map((it, j) => <li key={j}><Inline text={it} base={base} /></li>)}</ol>;
          case "note":
            return (
              <div key={k} className={`callout${b.tone === "warn" ? " warn" : ""}`}>
                {b.tone === "warn" ? <TriangleAlert aria-hidden="true" /> : <Info aria-hidden="true" />}
                <span><Inline text={b.text} base={base} /></span>
              </div>
            );
          case "code": return <pre key={k}><code>{b.text}</code></pre>;
          case "table":
            return (
              <div key={k} className="table-wrap">
                <table className="data">
                  <thead><tr>{b.head.map((c, j) => <th key={j}><Inline text={c} base={base} /></th>)}</tr></thead>
                  <tbody>{b.rows.map((r, j) => <tr key={j}>{r.map((c, m) => <td key={m}><Inline text={c} base={base} /></td>)}</tr>)}</tbody>
                </table>
              </div>
            );
        }
      })}
    </div>
  );
}

/** Títulos "##" do artigo, para o sumário lateral. */
export function headings(source: string): { id: string; text: string }[] {
  return parse(source).flatMap((b) => (b.kind === "h2" ? [{ id: b.id, text: b.text }] : []));
}

/** Texto corrido sem marcação, para a busca. */
export function plainText(source: string): string {
  return source
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[#>*`|_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
